import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  FiUser,
  FiCalendar,
  FiClock,
  FiRepeat,
  FiPauseCircle,
  FiXCircle,
  FiPlayCircle,
  FiTag,
} from 'react-icons/fi';
import Drawer from '../../components/ui/Drawer';
import Button from '../../components/ui/Button';
import StatusPill from '../../components/ui/StatusPill';
import Badge from '../../components/ui/Badge';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import ErrorBanner from '../../components/ui/ErrorBanner';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import CurrencyDisplay from '../../components/shared/CurrencyDisplay';
import DateDisplay from '../../components/shared/DateDisplay';
import { getSubscription, updateSubscriptionStatus } from '../../api/subscriptions';
import { extractErrorMessage } from '../../api/client';
import { SubscriptionStatus } from '../../enums';

function MetaItem({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-md bg-slate-100 text-ink-muted">
        <Icon size={14} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-medium uppercase tracking-wide text-ink-muted">
          {label}
        </div>
        <div className="mt-0.5 text-sm text-ink">{children}</div>
      </div>
    </div>
  );
}

const ACTION_CONFIGS = {
  PAUSE: {
    targetStatus: SubscriptionStatus.PAUSED,
    label: 'Pause',
    icon: FiPauseCircle,
    variant: 'secondary',
    confirmTitle: 'Pause this subscription?',
    confirmMessage:
      'The subscription will be paused. No new billing will occur until it is reactivated.',
    confirmLabel: 'Pause',
    toast: 'Subscription paused',
  },
  CANCEL: {
    targetStatus: SubscriptionStatus.CANCELLED,
    label: 'Cancel',
    icon: FiXCircle,
    variant: 'danger',
    confirmTitle: 'Cancel this subscription?',
    confirmMessage:
      'This will cancel the subscription permanently. It cannot be reactivated afterwards.',
    confirmLabel: 'Cancel Subscription',
    toast: 'Subscription cancelled',
  },
  REACTIVATE: {
    targetStatus: SubscriptionStatus.ACTIVE,
    label: 'Reactivate',
    icon: FiPlayCircle,
    variant: 'success',
    confirmTitle: 'Reactivate this subscription?',
    confirmMessage: 'The subscription will return to ACTIVE and resume billing.',
    confirmLabel: 'Reactivate',
    toast: 'Subscription reactivated',
  },
};

function availableActions(status) {
  switch (status) {
    case SubscriptionStatus.ACTIVE:
      return ['PAUSE', 'CANCEL'];
    case SubscriptionStatus.PAUSED:
      return ['REACTIVATE', 'CANCEL'];
    case SubscriptionStatus.EXPIRED:
      return ['REACTIVATE'];
    case SubscriptionStatus.CANCELLED:
    default:
      return [];
  }
}

export default function SubscriptionDetailDrawer({
  open,
  subscriptionId,
  onClose,
  fallback,
}) {
  const qc = useQueryClient();
  const [pendingAction, setPendingAction] = useState(null);

  const {
    data: fetched,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['subscriptions', subscriptionId],
    queryFn: () => getSubscription(subscriptionId),
    enabled: !!subscriptionId && open,
    initialData:
      fallback && fallback.id === subscriptionId ? fallback : undefined,
  });

  const subscription =
    fetched || (fallback?.id === subscriptionId ? fallback : null);

  const statusMutation = useMutation({
    mutationFn: ({ status }) => updateSubscriptionStatus(subscriptionId, status),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['subscriptions'] });
      qc.invalidateQueries({ queryKey: ['subscriptions', subscriptionId] });
      qc.invalidateQueries({ queryKey: ['revenue'] });
      const cfg = ACTION_CONFIGS[variables?.actionKey];
      toast.success(cfg?.toast || 'Subscription updated');
      setPendingAction(null);
    },
    onError: (err) => {
      toast.error(extractErrorMessage(err));
      setPendingAction(null);
    },
  });

  const actions = availableActions(subscription?.status);
  const activeConfig = pendingAction ? ACTION_CONFIGS[pendingAction] : null;

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        width="lg"
        title={
          subscription
            ? subscription.planName || `Subscription #${subscription.id}`
            : 'Subscription'
        }
        footer={
          actions.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              {actions.map((key) => {
                const cfg = ACTION_CONFIGS[key];
                return (
                  <Button
                    key={key}
                    variant={cfg.variant}
                    icon={cfg.icon}
                    onClick={() => setPendingAction(key)}
                    loading={
                      statusMutation.isLoading &&
                      statusMutation.variables?.actionKey === key
                    }
                    disabled={statusMutation.isLoading}
                  >
                    {cfg.label}
                  </Button>
                );
              })}
            </div>
          )
        }
      >
        {isLoading && !subscription ? (
          <LoadingSpinner label="Loading subscription..." />
        ) : error && !subscription ? (
          <ErrorBanner
            error={error}
            title="Failed to load subscription"
            onRetry={refetch}
          />
        ) : !subscription ? (
          <div className="py-10 text-center text-sm text-ink-muted">
            Subscription not found.
          </div>
        ) : (
          <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-gradient-to-br from-primary/5 to-accent/5 p-4">
              <div className="min-w-0">
                <div className="text-[10px] font-medium uppercase tracking-wide text-ink-muted">
                  Plan
                </div>
                <div className="truncate text-lg font-semibold text-ink">
                  {subscription.planName || `#${subscription.id}`}
                </div>
                <div className="mt-1 text-xs text-ink-muted">
                  ID: #{subscription.id}
                </div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <StatusPill value={subscription.status} />
                <Badge tone={subscription.autoRenew ? 'success' : 'neutral'}>
                  Auto-Renew: {subscription.autoRenew ? 'On' : 'Off'}
                </Badge>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <MetaItem icon={FiUser} label="Customer ID">
                {subscription.customerId != null
                  ? `#${subscription.customerId}`
                  : '—'}
              </MetaItem>
              <MetaItem icon={FiRepeat} label="Billing Cycle">
                <Badge tone="info">
                  {String(subscription.billingCycle || '—').replace(/_/g, ' ')}
                </Badge>
              </MetaItem>
              <MetaItem icon={FiCalendar} label="Start Date">
                <DateDisplay value={subscription.startDate} />
              </MetaItem>
              <MetaItem icon={FiClock} label="End Date">
                <DateDisplay value={subscription.endDate} />
              </MetaItem>
              <MetaItem icon={FiTag} label="Amount per Cycle">
                <span className="text-sm font-semibold text-primary">
                  <CurrencyDisplay value={subscription.amount} />
                </span>
              </MetaItem>
              <MetaItem icon={FiCalendar} label="Created">
                <DateDisplay value={subscription.createdAt} withTime />
              </MetaItem>
            </div>

            <section className="rounded-lg border border-slate-200 bg-white">
              <div className="border-b border-slate-100 px-4 py-3">
                <h3 className="text-sm font-semibold text-ink">Billing Summary</h3>
              </div>
              <div className="divide-y divide-slate-100 px-4">
                <div className="flex items-center justify-between py-2">
                  <span className="text-xs font-medium uppercase tracking-wide text-ink-muted">
                    Amount per Cycle
                  </span>
                  <span className="text-sm font-medium text-ink">
                    <CurrencyDisplay value={subscription.amount} />
                  </span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-xs font-medium uppercase tracking-wide text-ink-muted">
                    Cycle
                  </span>
                  <span className="text-sm text-ink">
                    {String(subscription.billingCycle || '—').replace(
                      /_/g,
                      ' ',
                    )}
                  </span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-xs font-medium uppercase tracking-wide text-ink-muted">
                    Status
                  </span>
                  <StatusPill value={subscription.status} />
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-xs font-medium uppercase tracking-wide text-ink-muted">
                    Auto-Renew
                  </span>
                  <Badge tone={subscription.autoRenew ? 'success' : 'neutral'}>
                    {subscription.autoRenew ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>
              </div>
            </section>
          </div>
        )}
      </Drawer>

      <ConfirmDialog
        open={!!pendingAction}
        title={activeConfig?.confirmTitle}
        message={activeConfig?.confirmMessage}
        confirmLabel={activeConfig?.confirmLabel}
        variant={activeConfig?.variant === 'success' ? 'primary' : activeConfig?.variant}
        loading={statusMutation.isLoading}
        onConfirm={() =>
          activeConfig &&
          statusMutation.mutate({
            status: activeConfig.targetStatus,
            actionKey: pendingAction,
          })
        }
        onClose={() => !statusMutation.isLoading && setPendingAction(null)}
      />
    </>
  );
}
